<template>
  <div class="bottom-nav fixed bottom-0 z-[25] flex justify-center px-4 py-2 md:justify-start" :class="[barVisibilityClass, isSideOpen ? 'w-[calc(100vw_-_400px)]' : 'w-full']">
    <div class="mx-auto flex w-full max-w-[1136px] items-center justify-between">
      <!-- 情報部分: PCのみ表示 -->
      <div class="hidden items-center justify-start gap-4 font-Spectral text-[17px] font-normal leading-6 text-light-fg-default md:flex">
        <div class="inline-flex items-center gap-1">
          <img class="h-5 w-5" src="@/assets/icon/wabunka/location_on.svg" />
          <NuxtLink
            v-if="destinationHubPath"
            class="whitespace-nowrap text-gold1 underline decoration-transparent underline-offset-4 transition-colors hover:decoration-gold4"
            :to="destinationHubPath"
            >{{ destinationTagName }}</NuxtLink
          >
          <p v-else class="whitespace-nowrap">{{ destinationTagName }}</p>
        </div>
        <div v-if="formattedPrice" class="inline-flex items-center gap-1">
          <img class="h-5 w-5" src="@/assets/icon/wabunka/wallet.svg" />
          <span>{{ formattedPrice }}</span>
        </div>
        <div class="inline-flex items-center gap-1">
          <img class="h-5 w-5" src="@/assets/icon/wabunka/schedule.svg" />
          <span>{{ experience.experienceDuration?.value }} {{ experience.experienceDuration?.unit }}</span>
        </div>
        <div class="inline-flex items-center gap-1">
          <img class="h-5 w-5" src="@/assets/icon/group.svg" />
          <span>Private: {{ experience.minGuestsPerReservation }}-{{ experience.maxGuests }}</span>
        </div>
      </div>
      <!-- ボタン: PC/SP共通 -->
      <div class="w-full shrink-0 md:ml-auto md:max-w-[368px]">
        <AtomsPrimaryButton class="w-full text-[15px] md:!px-12" :on-click="onClickCheckAvailability">Check availability</AtomsPrimaryButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import AtomsPrimaryButton from '@/components/atoms/PrimaryButton/PrimaryButton.vue';
import type { WabunkaExperienceViewModel } from '@/models/wabunka/wabunkaExperience';
import { useChatPanel } from '@/wabunka-src/features/Chat/composables/general/useChatPanel';
import { displayPrice } from '@/wabunka-src/features/Experience/models/experience';
import { displayDestinationTagName } from '@/wabunka-src/features/Experience/models/wabunkaExperienceTag';
import { buildDestinationHubPath } from '@/wabunka-src/features/Experience/models/wabunkaExperienceTagHubLink';

const { isSideOpen } = useChatPanel();

interface Props {
  experience: WabunkaExperienceViewModel;
  shouldShow: boolean;
  isScrollingUp: boolean;
  isScrollingDown: boolean;
  onClickCheckAvailability: () => void;
}
const props = defineProps<Props>();

// 一度でも下スクロールしたかの検出（初期表示時のアニメーション制御用）
const initialScrollY = ref(0);
const hasScrolledDown = ref(false);

const handleScroll = () => {
  const currentScrollY = window.scrollY;
  if (currentScrollY > initialScrollY.value + 10 && !hasScrolledDown.value) {
    hasScrolledDown.value = true;
    window.removeEventListener('scroll', handleScroll);
  }
};

// スクロール監視を開始（SSR対策でonMounted内で実行）
onMounted(() => {
  initialScrollY.value = window.scrollY;
  window.addEventListener('scroll', handleScroll);
});

// スクロール監視を解除（メモリリーク防止）
onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

// 初期表示 → 下スクロールで非表示 → 上スクロールで再表示
const barVisibilityClass = computed(() => {
  if (props.isScrollingDown) return 'is-hidden';
  if (hasScrolledDown.value && props.isScrollingUp && props.shouldShow) return 'is-active';
  return '';
});

const destinationHubPath = computed(() => buildDestinationHubPath(props.experience.tags));

const destinationTagName = computed(() => {
  return displayDestinationTagName(props.experience.tags);
});

const formattedPrice = computed(() => {
  return displayPrice(props.experience);
});
</script>

<style scoped>
.bottom-nav {
  background-color: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  transition:
    transform 0.3s ease,
    opacity 0.3s ease,
    width 0.3s ease;
  &.is-active {
    animation: slideUp 0.3s;
  }
  &.is-hidden {
    transform: translateY(100%);
    opacity: 0;
    pointer-events: none;
  }
}
@keyframes slideUp {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
